import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { getLatestTosAcceptance, recordTosAcceptance } from "@/lib/tos.functions";
import { LEGAL_VERSIONS } from "@/lib/legal-versions";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";

type Role = "bartender" | "venue" | "admin";

/**
 * Intercepts signed-in sessions when the current legal versions
 * (terms/privacy/contractor) are newer than what the user last accepted.
 * Renders a blocking modal that records a fresh acceptance before releasing
 * the app back to the user.
 */
export function TosReacceptGate() {
  const fetchLatest = useServerFn(getLatestTosAcceptance);
  const record = useServerFn(recordTosAcceptance);
  const [needsReaccept, setNeedsReaccept] = useState(false);
  const [role, setRole] = useState<Role | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return;
        const { acceptance } = await fetchLatest();
        if (cancelled) return;

        // Derive role from profile as fallback if no acceptance row yet.
        const { data: prof } = await supabase
          .from("profiles")
          .select("role")
          .eq("id", session.user.id)
          .maybeSingle();
        const r = (acceptance?.role as Role | undefined) ?? (prof?.role as Role | undefined) ?? null;
        setRole(r);

        if (!acceptance) return; // brand-new users accept at signup
        const stale =
          acceptance.terms_version !== LEGAL_VERSIONS.terms ||
          acceptance.privacy_version !== LEGAL_VERSIONS.privacy ||
          (r === "bartender" && acceptance.contractor_terms_version !== LEGAL_VERSIONS.contractorAgreement);
        if (stale) setNeedsReaccept(true);
      } catch {
        // fail-open: never lock users out on a check error
      }
    })();
    return () => { cancelled = true; };
  }, [fetchLatest]);

  const submit = async () => {
    if (!role || !checked) return;
    setSubmitting(true);
    const r = await record({ data: { role, context: "reaccept" } });
    setSubmitting(false);
    if ("ok" in r && r.ok) {
      toast.success("Thanks — your acceptance has been recorded.");
      setNeedsReaccept(false);
    } else {
      toast.error("Could not record your acceptance. Please try again.");
    }
  };

  if (!needsReaccept) return null;

  return (
    <Dialog open modal>
      <DialogContent className="max-w-lg" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Updated Terms &amp; Privacy Notice</DialogTitle>
          <DialogDescription>
            We've updated our legal terms. Please review and accept the new versions to continue using The Hospitality Reserve.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <ul className="list-disc pl-5 text-muted-foreground">
            <li><Link to="/legal/terms" target="_blank" className="underline">Terms of Service</Link> — v{LEGAL_VERSIONS.terms}</li>
            <li><Link to="/legal/privacy" target="_blank" className="underline">Privacy Notice</Link> — v{LEGAL_VERSIONS.privacy}</li>
            {role === "bartender" && (
              <li><Link to="/legal/contractor-agreement" target="_blank" className="underline">Contractor Agreement</Link> — v{LEGAL_VERSIONS.contractorAgreement}</li>
            )}
          </ul>
          <label className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
            <span>I have read and accept the updated terms above.</span>
          </label>
        </div>
        <div className="flex justify-end">
          <Button disabled={!checked || submitting || !role} onClick={submit}>
            {submitting ? "Recording…" : "Accept &amp; continue"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
